// The typed connection scope union (contracts.md registry row "ConnectionScope typed union"; ADR-0016).
//
// Parses `connections.scope` jsonb into a discriminated union so the least-privilege review gate can
// diff requested-vs-granted scopes MECHANICALLY (ADR-0016 consequences). Both host sides are kept
// EXPLICIT (GitHub App permissions vs GitLab token scopes), never collapsed to a lossy shared shape.
// The scope-diff LOGIC lives in @nightcrew/connections (Wave 2); this package owns only the shape.
import { z } from "zod";
import { Cloud, ConnectionMode } from "./enums.js";
import { NonEmptyString } from "./common.js";
import { GitHostPermissions } from "../ports/git-host.js";
/**
 * GitLab's least-privilege project/group token scopes - the mapped equivalent of GitHostPermissions
 * (ADR-0016 §5: GitLab uses OAuth + short-lived project/group access tokens). STRICT and NEVER an
 * owner/maintainer administrative scope; both sides of the seam stay explicit.
 */
export const GitLabPermissions = z.strictObject({
    repository: z.literal("write"), // GitHub `contents`
    merge_requests: z.literal("write"), // GitHub `pull_requests`
    issues: z.literal("write"),
    pipelines: z.literal("write"), // GitHub `checks`/`actions`/`workflows`
    metadata: z.literal("read"),
});
export const GitHubConnectionScope = z.strictObject({
    platform: z.literal("github"),
    installationId: NonEmptyString,
    repositoryIds: z.array(z.string()),
    permissions: GitHostPermissions,
});
export const GitLabConnectionScope = z.strictObject({
    platform: z.literal("gitlab"),
    installationRef: NonEmptyString,
    projectIds: z.array(z.string()),
    permissions: GitLabPermissions,
});
export const CloudConnectionScope = z.strictObject({
    platform: z.literal("cloud"),
    cloud: Cloud,
    region: NonEmptyString,
    mode: ConnectionMode,
    /** The assumed role's reference under federation; null for a scoped-token connection. */
    roleRef: NonEmptyString.nullable(),
});
/** The parsed shape of a connection's granted (or requested) scope, discriminated by platform. */
export const ConnectionScope = z.discriminatedUnion("platform", [
    GitHubConnectionScope,
    GitLabConnectionScope,
    CloudConnectionScope,
]);
//# sourceMappingURL=connection-scope.js.map